import { isNullable } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { useLocale } from 'containers/App/LocaleContext';
import { type ChangeEvent, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';

export interface UploadedCa {
  id: string;
  name: string;
}

interface Props {
  onClose: () => void;
  onUploaded: (ca: UploadedCa) => void | Promise<void>;
}

export const CaUploadModal = ({ onClose, onUploaded }: Props) => {
  const msg = useLocale().settings.webHooks.form;
  const apiRequest = useApi().request;
  const { showNotification } = useNotifications();

  const [name, setName] = useState('');
  const [content, setContent] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNullable(file)) {
      setContent(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const result = evt.target?.result;
      setContent(typeof result === 'string' ? result : null);
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!name || !content) {
      return;
    }
    setUploading(true);
    try {
      const created: UploadedCa = await apiRequest({
        url: '/settings/ca',
        method: 'POST',
        data: { content, name },
      });
      showNotification({ type: 'success', message: msg.caCertUploadSuccess });
      await onUploaded(created);
      onClose();
    } catch {
      showNotification({ type: 'danger', message: msg.caCertUploadError });
    } finally {
      setUploading(false);
    }
  };

  return (
    <OverlayModal onClose={onClose}>
      <OverlayModalHeader>
        <h4 className='box-title'>{msg.caCertUploadTitle}</h4>
      </OverlayModalHeader>
      <OverlayModalBody>
        <div className='form-group'>
          <label className='block' htmlFor='webhook-ca-name'>
            {msg.caCertUploadName}
          </label>
          <input
            id='webhook-ca-name'
            className='form-control'
            type='text'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <input type='file' onChange={handleFileChange} />
        </div>
      </OverlayModalBody>
      <OverlayModalFooter>
        <Button
          disabled={!name || !content}
          loading={uploading}
          type='button'
          variant='primary'
          className='mr-2'
          onClick={handleUpload}
        >
          {msg.caCertUpload}
        </Button>
        <Button type='button' variant='secondary' onClick={onClose}>
          {msg.btnCancel}
        </Button>
      </OverlayModalFooter>
    </OverlayModal>
  );
};
